import { TRANSITION_IN } from "./AnimationConstants";
import {
  TEXT_COLUMNS,
  TEXT_COLUMNS_GAP,
  TEXT_STYLE,
  GRAPHIC_STYLE_TYPE,
  GALLERY_AUTO_ADVANCE_TIME,
  GALLERY_TRANSITION,
  GALLERY_TRANSITION_DURATION,
  GALLERY_COLUMNS,
  GALLERY_ENFORCE_ORDER,
  GALLERY_IMAGE_PADDING,
  GALLERY_IMAGE_BORDER,
  GALLERY_FULL_SCREEN_BACKGROUND_COLOR,
  GALLERY_FULL_SCREEN_CONTROL_COLOR,
  GALLERY_NO_LOOP,
  GALLERY_DOTS,
  GALLERY_INACTIVE_IMAGES,
  GALLERY_IMAGE_BORDER_COLOR,
  GALLERY_IMAGE_BORDER_RADIUS,
  GALLERY_IMAGE_BORDER_WIDTH,
  GALLERY_DOTS_COLOR,
  GALLERY_DOTS_SELECTED_COLOR,
  GALLERY_DOTS_LOCATION,
  GALLERY_DOTS_SIZE,
  GALLERY_DOTS_GAP,
  GALLERY_DOTS_OFFSET,
  GALLERY_INACTIVE_IMAGES_BLUR,
  GALLERY_INACTIVE_IMAGES_OPACITY,
  GALLERY_INACTIVE_IMAGES_PRELOAD,
  GALLERY_INACTIVE_IMAGES_SCALE,
  ANIMATION_TYPE,
  ANIMATION_DURATION,
  ANIMATION_DELAY,
  ANIMATION_DIRECTION,
  BUTTON_BACKGROUND_COLOR,
  BUTTON_FILL_TYPE,
  BUTTON_PADDING,
  BUTTON_HEIGHT,
  BUTTON_WIDTH,
  BUTTON_HOVER_BACKGROUND_COLOR,
  BUTTON_HOVER_DURATION,
  BUTTON_HOVER_BORDER_COLOR,
  BUTTON_HOVER_BORDER_WIDTH,
  BUTTON_HOVER_PADDING,
  BUTTON_HOVER_BORDER_RADIUS,
  BUTTON_BORDER,
  SHADOW_STYLE,
  BORDER_RADIUS,
  BUTTON_BORDER_COLOR,
  BUTTON_BORDER_WIDTH,
  BUTTON_BORDER_STYLE,
  OPACITY,
  BLUR,
  ELEMENT_BACKGROUND_FILL_TYPE,
  ELEMENT_BACKGROUND_COLOR,
  GRADIENT_COLOR_1,
  GRADIENT_COLOR_2,
  GRADIENT_ROTATION,
} from "./MergeConstants";

/**
 * Used to map properties stored in json to actual css properties or inline style properties that can be used by React
 *
 */
const PROPERTY_MAPS = {
  DEFAULT_LINK_STYLE: {
    tD: "underline",
  },

  PROP_TEXT_COLOR: "c",
  PROP_COLOR: "color",
  PROP_BG_COLOR_V2: "bgColor",
  PROP_BG_COLOR: "bgcolor",
  PROP_BORDER_COLOR: "bC",
  PROP_FONT: "font",
  PROP_TEXT_DECORATION: "tD",
  PROP_DROP_SHADOW: "shadow",

  PROP_LOCKING_H: "lockH",
  PROP_LOCKING_V: "lockV",

  CSS_UNITS_REG_EX: /px|em|%/g,

  // all showit data model properties mapped to reach style props
  SHOWIT_TO_REACT_PROP_MAP: {
    bgcolor: "backgroundColor",
    bgColor: "backgroundColor",
    backgroundImage: "backgroundImage",
    c: "color",
    color: "color",
    lC: "textTransform",
    lH: "lineHeight",
    lS: "letterSpacing",
    size: "fontSize",
    tA: "textAlign",
    font: "fontFamily",
    family: "fontFamily",
    weight: "fontWeight",
    style: "fontStyle",
    columnCnt: "columnCount",
    columnGap: "columnGap",
    bC: "borderColor",
    bW: "borderWidth",
    bS: "borderStyle",
    radius: "borderRadius",
    mB: "marginBottom",
    tD: "textDecoration",
    repeat: "backgroundRepeat",
    pos: "backgroundPosition",
    opacity: "opacity",
    scale: "backgroundSize",
    padding: "padding",
  },

  SHOWIT_TO_CSS_PROP_MAP: {
    bgcolor: "background-color",
    bgColor: "background-color",
    backgroundImage: "background-image",
    c: "color",
    color: "color",
    lC: "text-transform",
    lH: "line-height",
    lS: "letter-spacing",
    size: "font-size",
    tA: "text-align",
    font: "font-family",
    family: "font-family",
    weight: "font-weight",
    style: "font-style",
    columnCnt: "column-count",
    columnGap: "column-gap",
    mB: "margin-bottom",
    bC: "border-color",
    bW: "border-width",
    bS: "border-style",
    radius: "border-radius",
    tD: "text-decoration",
    repeat: "background-repeat",
    pos: "background-position",
    opacity: "opacity",
    scale: "background-size",
    padding: "padding",
  },

  SHOWIT_PROPS_DEFAULT_UNITS: {
    lS: "em",
    size: "px",
    mB: "px",
    bW: "px",
    columnGap: "px",
  },

  STYLE_PROPS_THEME: ["c", "lC", "lH", "lS", "size", "tA", "font"],

  STYLE_PROPS_THEME_TEXT: ["c", "lC", "lH", "lS", "size", "tA", "font", "mB"],
  STYLE_PROPS_THEME_INLINE_LINK: ["c", "tD", "hC", "hTd"],

  STYLE_PROPS_LINK: ["c", "tD"],

  STYLE_PROPS_INLINE: [
    "bgcolor",
    "c",
    "lC",
    "lH",
    "lS",
    "size",
    "tA",
    "font",
    "bC",
    "bW",
    "bS",
    "columnCnt",
    "columnGap",
    "shadow",
  ],

  STYLE_PROPS_BG: ["bgColor"],

  STYLE_PROPS_BG_OVERRIDE: { color: "bgColor" }, // override to map background 'color' to 'background-color'

  BG_SCALE_VALUE_MAP: {
    cover: "cover",
    fit: "contain",
    native: "auto",
  },

  BG_POSITION_VALUE_MAP: {
    cm: "center",
    lt: "left top",
    ct: "center top",
    rt: "right top",
    lm: "left center",
    rm: "right center",
    lb: "left bottom",
    cb: "center bottom",
    rb: "right bottom",
  },

  BG_REPEAT_VALUE_MAP: {
    none: "no-repeat",
    xy: "repeat",
    y: "repeat-y",
    x: "repeat-x",
  },

  IS_PARENT_PROP: {
    fillType: true,
    border: true,
    shadow: true,
  },

  EFFECTS_PROPS: ["o", "border", "shadow"],

  ELEMENT_POSITION: ["x", "y", "w", "h", "a"],

  ICON_PROPS: ["c"],

  // properties specific to text style
  TEXT_STYLE_PROPS: ["c", "lC", "lH", "lS", "size", "tA", "font", "columnCnt", "columnGap", "over"],
  TEXT_TRANSFORM_PROP: "lC",

  // properties specific to simple element
  SIMPLE_PROPS: ["bgcolor", "bC", "bW", "bS", "fillType", "gc1", "gc2", "gr", "padding"],
  SIMPLE_SYNC_PROPS: ["bC", "bW", "bS", "fillType", "padding"],

  // properties specific to line element
  LINE_PROPS: ["h", "c", "dW", "dF", "lineStyle", "lineCap"],

  // properties specific to drop shadows
  DROP_SHADOW_PROPS: ["style", "sc", "sx", "sy", "sb"],

  // properties specific to social grid element
  SOCIAL_GRID_PROPS: ["h", "rows", "alignment", "gap", "autoGap"],
  SOCIAL_GRID_SYNC_PROPS: ["rows", "alignment", "gap", "autoGap", "overflow"],

  HOVER_PROPS: {
    COMMON: ["o", "dur", "type"],
    ICON: ["o", "dur", "type", "c"],
    SIMPLE: ["type", "o", "dur", "c", "bW", "bC"],
    TEXT: ["type", "lS", "ul", "size", "font", "c", "dur", "o"],
  },

  ELEMENT_FILL_PROPS: [
    ELEMENT_BACKGROUND_FILL_TYPE,
    ELEMENT_BACKGROUND_COLOR,
    GRADIENT_COLOR_1,
    GRADIENT_COLOR_2,
    GRADIENT_ROTATION,
  ],

  BUTTON_SYNC_PROPS: [
    BUTTON_FILL_TYPE,
    BUTTON_PADDING,
    BUTTON_BORDER_COLOR,
    BUTTON_BORDER_WIDTH,
    BUTTON_BORDER_STYLE,
    SHADOW_STYLE,
    BUTTON_PADDING,
    BORDER_RADIUS,
    OPACITY,
    BLUR,
  ],

  GALLERY_SYNC_PROPS: [
    GRAPHIC_STYLE_TYPE,
    GALLERY_AUTO_ADVANCE_TIME,
    GALLERY_TRANSITION,
    GALLERY_TRANSITION_DURATION,
    GALLERY_COLUMNS,
    GALLERY_ENFORCE_ORDER,
    GALLERY_IMAGE_PADDING,
    GALLERY_IMAGE_BORDER,
    GALLERY_FULL_SCREEN_BACKGROUND_COLOR,
    GALLERY_FULL_SCREEN_CONTROL_COLOR,
    GALLERY_NO_LOOP,
    GALLERY_DOTS,
    GALLERY_INACTIVE_IMAGES,
  ],

  ELEMENT_TRANSITION_SYNC_PROPS: [`${TRANSITION_IN}.${ANIMATION_TYPE}`],
  ELEMENT_TRANSITION_PROPS: [
    `${TRANSITION_IN}.${ANIMATION_TYPE}`,
    `${TRANSITION_IN}.${ANIMATION_DURATION}`,
    `${TRANSITION_IN}.${ANIMATION_DELAY}`,
    `${TRANSITION_IN}.${ANIMATION_DIRECTION}`,
  ],

  STYLE_PROPS_THEME_BUTTONS: [
    "bgcolor",
    "fillType",
    "padding",
    "height",
    "width",
    "border.color",
    "border.width",
    "border.style",
    "border.radius",
    "border.rad",
    "bC",
    "bW",
    "bS",
    "o",
    "blur",
    "shadow.style",
    "hover.bgcolor",
    "hover.fillType",
    "hover.dur",
    "hover.border.color",
    "hover.border.width",
    "hover.border.style",
    "hover.border.radius",
    "hover.border.rad",
    "hover.bC",
    "hover.bW",
    "hover.bS",
    "hover.o",
    "hover.blur",
    "hover.padding",
    "hover.gc1",
    "hover.gc2",
    "hover.gr",
    "hover.text.font",
    "hover.text.c",
    "hover.text.lC",
    "hover.text.lH",
    "hover.text.lS",
    "hover.text.size",
  ],

  STYLE_PROPS_BUTTON_TEXT: [
    // "text.style",
    "text.c",
    "text.lC",
    "text.lH",
    "text.lS",
    "text.size",
    // "text.tA",
    "text.font",
    // "text.columnCnt",
    // "text.columnGap",
    // "text.over",
    // "text.styles.link.c",
    // "text.styles.link.tD",
    // "text.styles.link.hC",
    // "text.styles.link.hTd",
  ],
};

PROPERTY_MAPS.TEXT_NESTED_INLINE_LINK = PROPERTY_MAPS.STYLE_PROPS_THEME_INLINE_LINK.map(
  (prop) => `link.${prop}`,
);

PROPERTY_MAPS.TEXT_NESTED_STYLES_INLINE_LINK = PROPERTY_MAPS.TEXT_NESTED_INLINE_LINK.map(
  (prop) => `styles.${prop}`,
);

PROPERTY_MAPS.TEXT_PROPS = PROPERTY_MAPS.TEXT_STYLE_PROPS;

PROPERTY_MAPS.GATHERABLE_TEXT_PROPS = [
  TEXT_STYLE,
  ...PROPERTY_MAPS.TEXT_PROPS,
  ...PROPERTY_MAPS.TEXT_NESTED_STYLES_INLINE_LINK,
];

PROPERTY_MAPS.TEXT_SYNC_PROPS = PROPERTY_MAPS.GATHERABLE_TEXT_PROPS.filter(
  (propName) => ![TEXT_COLUMNS, TEXT_COLUMNS_GAP].includes(propName),
);

PROPERTY_MAPS.STYLE_PROPS_BUTTON = ["fillType", "padding", "border", "shadow", "o"];

PROPERTY_MAPS.GALLERY_PROPS = [
  ...PROPERTY_MAPS.GALLERY_SYNC_PROPS.filter(
    // filter direct parent props, otherwise causes double writes
    (propName) => ![GALLERY_IMAGE_BORDER, GALLERY_DOTS, GALLERY_INACTIVE_IMAGES].includes(propName),
  ),
  GALLERY_IMAGE_BORDER_COLOR,
  GALLERY_IMAGE_BORDER_RADIUS,
  GALLERY_IMAGE_BORDER_WIDTH,
  GALLERY_DOTS_COLOR,
  GALLERY_DOTS_SELECTED_COLOR,
  GALLERY_DOTS_LOCATION,
  GALLERY_DOTS_SIZE,
  GALLERY_DOTS_GAP,
  GALLERY_DOTS_OFFSET,
  GALLERY_INACTIVE_IMAGES_BLUR,
  GALLERY_INACTIVE_IMAGES_OPACITY,
  GALLERY_INACTIVE_IMAGES_PRELOAD,
  GALLERY_INACTIVE_IMAGES_SCALE,
];

export default PROPERTY_MAPS;
